import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'

import { Button } from '@/components/ui/Button'
import { downloadAutotekaPdf } from '@/api/inspection-leads'
import { formatElapsedProgressive } from '@/utils/format'
import { leadPipelineUi, type InspectionLeadRow } from '@/types/inspection-lead'
import { hasAutotekaVehicleId, isAutotekaWaiting } from '@/components/inspections/leadDetail'


// Сколько уже ждём ответа (тикает раз в секунду): у Автотеки нет срока,
// поэтому прямой таймер, а не обратный отсчёт до 45-минутного предела.
function ElapsedTimer({ from }: { from: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])
  return <span className="tabular-nums">{formatElapsedProgressive(now - new Date(from).getTime())}</span>
}

/**
 * Блок «Автотека» в палитре: кнопка запроса, ожидание с таймером, ошибка.
 * Одна механика для всех воронок — состояние запроса в лиде (autoteka_state),
 * edge enrich-lead-autoteka ищет по объявлению Avito или VIN/госномеру
 * (lead_pipelines.autoteka_mode), cron на сервере дожимает отчёт раз в минуту.
 * С какого этапа можно запрашивать — LEAD_PIPELINE_UI.autotekaFromStatus.
 * `queued`: лид только что переведён в этап автозапроса (autotekaAutoOnStatus),
 * триггер в БД уже ставит запрос через pg_net (лаг до ~10 с). Показываем
 * «запрашивается» вместо кнопки, чтобы оператор не нажимал её вдогонку.
 */
export function AutotekaControl({
  lead,
  pending,
  queued = false,
  onRequest,
}: {
  lead: InspectionLeadRow
  pending: boolean
  queued?: boolean
  onRequest: () => void
}) {
  const { t } = useTranslation()
  const ui = leadPipelineUi(lead.pipeline)
  const st = lead.autoteka_state
  const hasVehicleId = hasAutotekaVehicleId(lead)
  const muted = 'text-xs text-[color:var(--stryzh-color-text-secondary)]'

  if (queued && !isAutotekaWaiting(lead)) {
    return (
      <div className="flex flex-col">
        <span className={muted}>{t('leads.detail.autotekaQueued')}</span>
        <span className={muted}>{t('leads.detail.autotekaQueuedHint')}</span>
      </div>
    )
  }
  if (isAutotekaWaiting(lead)) {
    return (
      <div className="flex flex-col">
        <span className={muted}>
          {t('leads.detail.sberAutotekaWaiting')}
          {lead.autoteka_requested_at ? <> · <ElapsedTimer from={lead.autoteka_requested_at} /></> : null}
        </span>
        <span className={muted}>{t('leads.detail.sberAutotekaWaitHint')}</span>
      </div>
    )
  }
  if (ui.autotekaFromStatus && lead.status !== ui.autotekaFromStatus) {
    return <span className={muted}>{t('leads.detail.sberAutotekaWrongStage')}</span>
  }
  return (
    <div className="flex flex-col gap-2">
      {st === 'failed' && lead.autoteka_error ? (
        <span className="text-xs text-red-300">{t('leads.detail.sberAutotekaFailed', { msg: lead.autoteka_error })}</span>
      ) : null}
      {hasVehicleId ? (
        <Button type="button" variant="control" disabled={pending} onClick={onRequest}>
          {pending
            ? t('leads.detail.requestingAutoteka')
            : st === 'done' || st === 'failed'
              ? t('leads.detail.sberAutotekaAgain')
              : t('leads.detail.sberAutotekaBtn')}
        </Button>
      ) : (
        <span className={muted}>{t('leads.detail.sberAutotekaNoVehicle')}</span>
      )}
    </div>
  )
}

/** Копирование ссылки на онлайн-отчёт Автотеки (autoteka_web_url). Ссылка —
 *  обычная строка, копируется синхронно в обработчике клика (Safari/iOS
 *  доволен, без ClipboardItem-плясок). */
export function CopyAutotekaLinkButton({ url }: { url: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  return (
    <Button
      type="button"
      variant="ghost"
      title={t('leads.detail.copyAutotekaTitle')}
      onClick={() => {
        void navigator.clipboard?.writeText(url)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
    >
      {copied ? t('leads.detail.copied') : t('leads.detail.copyLink')}
    </Button>
  )
}

/** «Скачать PDF»: из bucket autoteka-reports (выгружен в фоне после отчёта),
 *  иначе — свежий PDF через edge autoteka-report-pdf. */
export function AutotekaPdfButton({ leadId, storedPath }: { leadId: string; storedPath?: string | null }) {
  const { t } = useTranslation()
  const mut = useMutation({ mutationFn: () => downloadAutotekaPdf(leadId, storedPath) })
  return (
    <Button
      type="button"
      variant="ghost"
      disabled={mut.isPending}
      title={mut.isError ? t('leads.detail.autotekaPdfError', { reason: (mut.error as Error).message }) : undefined}
      onClick={() => mut.mutate()}
    >
      {mut.isPending
        ? t('leads.detail.autotekaPdfLoading')
        : mut.isError
          ? t('leads.detail.autotekaPdfRetry')
          : t('leads.detail.autotekaPdf')}
    </Button>
  )
}
